import { api } from '@/lib/api';
import type { CampaignStatus, CampaignMessageStatus, CampaignOrigin, CampaignAutomationType } from '@valiosascrm/shared';
import type { MessageContentV1 } from '@valiosascrm/shared';
import type {
  CampaignAudienceCondition,
  CampaignAudienceEntity,
  CampaignAudiencePreviewResult,
  CampaignMessagePreviewResult,
  CampaignSegmentationCatalog,
} from '@valiosascrm/shared/types';

// =============================================================================
// TYPES
// =============================================================================

export type {
  CampaignMessageStatus,
  CampaignStatus,
  CampaignOrigin,
  CampaignAutomationType,
} from '@valiosascrm/shared';
export type {
  CampaignAudienceCondition,
  CampaignAudienceEntity,
  CampaignAudiencePreviewResult,
  CampaignMessagePreviewResult,
  CampaignSegmentationField,
  CampaignSegmentationCatalog,
  CampaignSegmentationPreset,
  CampaignTemplateVariable,
} from '@valiosascrm/shared/types';

export interface CampaignTemplate {
  id: string;
  tenantId: string;
  name: string;
  content: string;
  document?: MessageContentV1;
  variables: string[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Campaign {
  id: string;
  tenantId: string;
  name: string;
  templateId: string | null;
  template?: CampaignTemplate | null;
  content: string;
  document?: MessageContentV1;
  status: CampaignStatus;
  // Story 61.4 — lote automático (planejador) × manual.
  origin?: CampaignOrigin;
  // Story 66.5 — mandada a partir de uma etapa de funil de marketing (null = campanha comum).
  marketingOrigin?: { funnelName: string | null; stageName: string | null } | null;
  automationType?: CampaignAutomationType | null;
  automationRunDate?: string | null;
  pausedAt?: string | null;
  scheduledAt: string | null;
  startedAt: string | null;
  completedAt: string | null;
  failedReason?: string | null;
  totalRecipients: number;
  sent: number;
  delivered: number;
  read: number;
  failed: number;
  audienceEntity?: CampaignAudienceEntity | null;
  audiencePreset?: string | null;
  audienceConditions?: CampaignAudienceCondition[] | null;
  audienceSnapshot?: unknown | null;
  totalExcludedOptOut?: number;
  totalExcludedMissingPhone?: number;
  estimatedDurationSeconds?: number | null;
  createdAt: string;
  updatedAt: string;
  _count?: { messages: number };
}

export interface CampaignMessage {
  id: string;
  campaignId: string;
  patientId: string;
  patient: {
    id: string;
    name: string;
    phone: string;
  };
  status: CampaignMessageStatus;
  sentAt: string | null;
  deliveredAt: string | null;
  readAt: string | null;
  error: string | null;
  createdAt: string;
  blockDeliveries?: CampaignBlockDelivery[];
}
export interface RetryCampaignMessageResult { resetBlocks: number; campaignMessageId: string }

export interface CampaignBlockDelivery {
  blockId: string;
  position: number;
  blockType: 'text' | 'image' | 'audio';
  status: 'pending' | 'processing' | 'sent' | 'delivered' | 'read' | 'failed' | 'indeterminate';
  attemptCount: number;
  lastAttemptAt: string | null;
  sentAt: string | null;
  deliveredAt: string | null;
  readAt: string | null;
  providerMessageId: string | null;
  errorCode: string | null;
  errorMessage: string | null;
}

export interface ReconcileCampaignMessageResult {
  reconciled: true;
  blockId: string;
  outcome: 'accepted' | 'rejected';
  resumed: boolean;
  campaignMessageId: string;
}

export interface CampaignWithMessages extends Campaign {
  messages: CampaignMessage[];
}

export type CampaignAudienceSegment =
  | 'all_active'
  | 'inactive_90d'
  | 'churn_risk'
  | 'birthdays_month'
  | 'recent_procedure'
  | 'receivables_due'
  | 'custom';

export interface CampaignAudiencePatient {
  id: string;
  name: string;
  phone: string;
  email: string | null;
  optOutMarketing: boolean;
}

export interface CampaignAudienceParams {
  segment?: CampaignAudienceSegment;
  search?: string;
  state?: string;
  city?: string;
  inactivityDays?: number;
  lastVisitDays?: number;
  minSpent?: number;
  maxSpent?: number;
  paymentStatus?: 'paid' | 'pending' | 'overdue';
  limit?: number;
}

export interface CampaignAudienceResult {
  segment: CampaignAudienceSegment;
  totalEligible: number;
  optOutExcluded: number;
  capped: boolean;
  recipientIds: string[];
  patients: CampaignAudiencePatient[];
}

export interface CreateCampaignData {
  name: string;
  content: string;
  document?: MessageContentV1;
  templateId?: string;
  scheduledAt?: string;
  recipientIds?: string[];
  audienceSnapshotToken?: string;
  messageIntervalSeconds?: number;
}

export interface UpdateCampaignDraftData {
  name?: string;
  content?: string;
  document?: MessageContentV1;
}

export interface PreviewCampaignAudienceData {
  entity: CampaignAudienceEntity;
  preset?: string;
  search?: string;
  conditions?: CampaignAudienceCondition[];
  limit?: number;
}

export interface PreviewCampaignMessageData {
  content: string;
  recipientSnapshotToken: string;
}

export interface CreateTemplateData {
  name: string;
  content: string;
  variables?: string[];
}

export interface UpdateTemplateData {
  name?: string;
  content?: string;
  document?: MessageContentV1;
  variables?: string[];
}

// =============================================================================
// TEMPLATES API
// =============================================================================

export async function getTemplates(): Promise<CampaignTemplate[]> {
  const { data } = await api.get<CampaignTemplate[]>('/campaigns/templates');
  return data;
}

export async function getTemplate(id: string): Promise<CampaignTemplate> {
  const { data } = await api.get<CampaignTemplate>(`/campaigns/templates/${id}`);
  return data;
}

export async function createTemplate(templateData: CreateTemplateData): Promise<CampaignTemplate> {
  const { data } = await api.post<CampaignTemplate>('/campaigns/templates', templateData);
  return data;
}

export async function updateTemplate(
  id: string,
  templateData: UpdateTemplateData,
): Promise<CampaignTemplate> {
  const { data } = await api.patch<CampaignTemplate>(`/campaigns/templates/${id}`, templateData);
  return data;
}

export async function deleteTemplate(id: string): Promise<{ message: string }> {
  const { data } = await api.delete<{ message: string }>(`/campaigns/templates/${id}`);
  return data;
}

// =============================================================================
// CAMPAIGNS API
// =============================================================================

/** Story 61.4 — o que a faixa do shell mostra (GET /campaigns/active, 61.3 AC6). */
export interface ActiveCampaign {
  id: string;
  name: string;
  origin: CampaignOrigin;
  automationType: CampaignAutomationType | null;
  status: CampaignStatus;
  totalRecipients: number;
  sent: number;
  failed: number;
  scheduledAt: string | null;
  startedAt: string | null;
  pausedAt: string | null;
  failedReason: string | null;
}

export async function listActiveCampaigns(): Promise<ActiveCampaign[]> {
  const { data } = await api.get<ActiveCampaign[]>('/campaigns/active');
  return data;
}

export async function getCampaigns(): Promise<Campaign[]> {
  const { data } = await api.get<Campaign[]>('/campaigns');
  return data;
}

export async function getCampaign(id: string): Promise<CampaignWithMessages> {
  const { data } = await api.get<CampaignWithMessages>(`/campaigns/${id}`);
  return data;
}

export async function getCampaignStats(id: string): Promise<Campaign> {
  const { data } = await api.get<Campaign>(`/campaigns/${id}/stats`);
  return data;
}

export async function getCampaignAudience(
  params: CampaignAudienceParams = {},
): Promise<CampaignAudienceResult> {
  const { data } = await api.get<CampaignAudienceResult>('/campaigns/audience', { params });
  return data;
}

export async function getSegmentationCatalog(): Promise<CampaignSegmentationCatalog> {
  const { data } = await api.get<CampaignSegmentationCatalog>('/campaigns/segmentation/catalog');
  return data;
}

export async function previewCampaignAudience(
  previewData: PreviewCampaignAudienceData,
): Promise<CampaignAudiencePreviewResult> {
  const { data } = await api.post<CampaignAudiencePreviewResult>(
    '/campaigns/audience/preview',
    previewData,
  );
  return data;
}

export async function previewCampaignMessage(
  previewData: PreviewCampaignMessageData,
): Promise<CampaignMessagePreviewResult> {
  const { data } = await api.post<CampaignMessagePreviewResult>(
    '/campaigns/message/preview',
    previewData,
  );
  return data;
}

export async function retryCampaignMessage(campaignId: string, messageId: string): Promise<RetryCampaignMessageResult> {
  const { data } = await api.post<RetryCampaignMessageResult>(`/campaigns/${campaignId}/messages/${messageId}/retry`);
  return data;
}

export async function reconcileCampaignMessage(
  campaignId: string,
  messageId: string,
  blockId: string,
  outcome: 'accepted' | 'rejected',
): Promise<ReconcileCampaignMessageResult> {
  const { data } = await api.post<ReconcileCampaignMessageResult>(
    `/campaigns/${campaignId}/messages/${messageId}/reconcile`,
    { blockId, outcome },
  );
  return data;
}

export async function createCampaign(campaignData: CreateCampaignData): Promise<Campaign> {
  const { data } = await api.post<Campaign>('/campaigns', campaignData);
  return data;
}

export async function updateCampaignDraft(id: string, campaignData: UpdateCampaignDraftData): Promise<Campaign> {
  const { data } = await api.patch<Campaign>(`/campaigns/${id}`, campaignData);
  return data;
}

export async function startCampaign(id: string): Promise<{ message: string; campaignId: string }> {
  const { data } = await api.post<{ message: string; campaignId: string }>(
    `/campaigns/${id}/start`,
  );
  return data;
}

export async function cancelCampaign(id: string): Promise<{ message: string }> {
  const { data } = await api.post<{ message: string }>(`/campaigns/${id}/cancel`);
  return data;
}

export async function pauseCampaign(id: string): Promise<{ message: string; campaignId: string }> {
  const { data } = await api.post<{ message: string; campaignId: string }>(
    `/campaigns/${id}/pause`,
  );
  return data;
}

export async function resumeCampaign(id: string): Promise<{ message: string; campaignId: string }> {
  const { data } = await api.post<{ message: string; campaignId: string }>(
    `/campaigns/${id}/resume`,
  );
  return data;
}

export async function previewMessage(
  content: string,
  patientId: string,
): Promise<{ preview: string }> {
  const { data } = await api.post<{ preview: string }>('/campaigns/preview', {
    content,
    patientId,
  });
  return data;
}
